import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { emailConfigService } from '../services/emailService';

const EmailConfigurationForm = ({ show, onHide, config, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    host: '',
    port: 587,
    secure: false,
    requireTLS: true,
    authUser: '',
    authPass: '',
    fromEmail: '',
    fromName: '',
    isDefault: false
  });
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (config) {
      // Editing existing config - don't load password
      setFormData({
        name: config.name || '',
        description: config.description || '',
        host: config.host || '',
        port: config.port || 587,
        secure: config.secure || false,
        requireTLS: config.requireTLS !== false,
        authUser: config.authUser || '',
        authPass: '', // Don't pre-fill password
        fromEmail: config.fromEmail || '',
        fromName: config.fromName || '',
        isDefault: config.isDefault || false
      });
    } else {
      // New config - reset form
      setFormData({
        name: '',
        description: '',
        host: '',
        port: 587,
        secure: false,
        requireTLS: true,
        authUser: '',
        authPass: '',
        fromEmail: '',
        fromName: '',
        isDefault: false
      });
    }
    setError(null);
    setSuccess(null);
    setTestResult(null);
  }, [config, show]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear test result when form changes
    if (testResult) setTestResult(null);
  };

  const handleTest = async () => {
    if (!formData.host || !formData.port || !formData.authUser || !formData.authPass) {
      setError('Please fill in host, port, username, and password to test connection');
      return;
    }

    try {
      setTesting(true);
      setError(null);
      setTestResult(null);

      const testData = {
        host: formData.host,
        port: parseInt(formData.port),
        secure: formData.secure,
        requireTLS: formData.requireTLS,
        authUser: formData.authUser,
        authPass: formData.authPass
      };

      const response = await emailConfigService.testNewConfiguration(testData);
      
      if (response.status) {
        setTestResult({ success: true, message: response.message });
        setSuccess('Connection test successful!');
      } else {
        setTestResult({ success: false, message: response.message });
        setError(response.message || 'Connection test failed');
      }
    } catch (err) {
      setTestResult({ success: false, message: err.message });
      setError(err.message || 'Failed to test connection');
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (config) {
        // Update existing
        await emailConfigService.updateConfiguration(config.id, formData);
        setSuccess('Email configuration updated successfully!');
      } else {
        // Create new
        await emailConfigService.createConfiguration(formData);
        setSuccess('Email configuration created successfully!');
      }

      setTimeout(() => {
        onSuccess();
        onHide();
      }, 1000);
    } catch (err) {
      setError(err.message || 'Failed to save email configuration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {config ? 'Edit Email Configuration' : 'Add Email Configuration'}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && !testResult && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}
          {testResult && (
            <Alert variant={testResult.success ? 'success' : 'danger'}>
              {testResult.success ? '✅ ' : '❌ '}
              {testResult.message}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Configuration Name *</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Gmail SMTP"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Optional description"
            />
          </Form.Group>

          <div className="row">
            <Form.Group className="mb-3 col-md-8">
              <Form.Label>SMTP Host *</Form.Label>
              <Form.Control
                type="text"
                name="host"
                value={formData.host}
                onChange={handleChange}
                required
                placeholder="e.g., smtp.gmail.com"
              />
            </Form.Group>

            <Form.Group className="mb-3 col-md-4">
              <Form.Label>Port *</Form.Label>
              <Form.Control
                type="number"
                name="port"
                value={formData.port}
                onChange={handleChange}
                required
                min="1"
                max="65535"
                placeholder="587"
              />
            </Form.Group>
          </div>

          <div className="row">
            <Form.Group className="mb-3 col-md-6">
              <Form.Check
                type="checkbox"
                name="secure"
                label="Use SSL (for port 465)"
                checked={formData.secure}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group className="mb-3 col-md-6">
              <Form.Check
                type="checkbox"
                name="requireTLS"
                label="Require TLS (for port 587)"
                checked={formData.requireTLS}
                onChange={handleChange}
              />
            </Form.Group>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>SMTP Username (Email) *</Form.Label>
            <Form.Control
              type="email"
              name="authUser"
              value={formData.authUser}
              onChange={handleChange}
              required
              placeholder="your-email@gmail.com"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>SMTP Password *</Form.Label>
            <Form.Control
              type="password"
              name="authPass"
              value={formData.authPass}
              onChange={handleChange}
              required={!config}
              placeholder={config ? "Leave blank to keep existing password" : "Enter password"}
            />
            {config && (
              <Form.Text className="text-muted">
                Leave blank to keep the existing password, or enter a new password to update it.
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>From Email Address *</Form.Label>
            <Form.Control
              type="email"
              name="fromEmail"
              value={formData.fromEmail}
              onChange={handleChange}
              required
              placeholder="sender@example.com"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>From Name</Form.Label>
            <Form.Control
              type="text"
              name="fromName"
              value={formData.fromName}
              onChange={handleChange}
              placeholder="Optional sender name"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              name="isDefault"
              label="Set as default configuration"
              checked={formData.isDefault}
              onChange={handleChange}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={handleTest}
            disabled={testing || loading}
          >
            {testing ? (
              <>
                <Spinner size="sm" className="me-2" />
                Testing...
              </>
            ) : (
              'Test Connection'
            )}
          </Button>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading || testing}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              config ? 'Update' : 'Create'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EmailConfigurationForm;

